/**
 * Domain model for signed digraphs.
 *
 * Models are stored in Redux (and as the primary keys of saved files) in this shape:
 *
 *   node: {
 *     id, label, title?, shape ('dot' | 'square' | 'ellipse' | 'box' | 'image'),
 *     // ID mode draws dot/square with the id; label mode draws ellipse/box/icon with wrapped text
 *     x, y, width?, height?, resized?: true, image?, color?: { background?, border? },
 *     physics?: false,
 *   }
 *   edge: {
 *     id, from, to, sign (1 | -1), title?, dashes?: boolean, color?: string,
 *     dist?: 'uniform' | 'weak' | 'moderate' | 'strong',  // interaction-strength distribution; omitted = uniform
 *     roundness: number,   // signed curvature; > 0 bows counter-clockwise, < 0 clockwise, 0 is straight
 *     loopAngle: number,   // radians; where a self effect sits around its node (0 = north, clockwise)
 *   }
 *   meta: { title?, description?, version?, authors?, date_created?, last_modified?, pinnedResults? }
 *
 * Files written by earlier versions of the app (and qmm's import routine) use
 * vis-network property names, e.g. the sign lives in `arrows.to.type`. `normalizeModel` reads
 * both forms and `toLegacyModel` writes both, so existing files, notebooks and the server keep
 * working unchanged.
 */

export const POSITIVE = 1;
export const NEGATIVE = -1;

export const SHAPES = ['dot', 'square', 'ellipse', 'box', 'image'];
export const DEFAULT_SHAPE = 'dot';

/** Default rendered size (px) per shape. `image` is the length of the shorter side. */
export const SHAPE_SIZES = {
  dot: { width: 54, height: 54 },
  square: { width: 52, height: 52 },
  ellipse: { width: 150, height: 80 },
  box: { width: 150, height: 80 },
  image: { width: 50, height: 50 },
};

export const DEFAULT_LOOP_ANGLE = Math.PI / 4;
/** vis-network default bow on a two-way pair. We keep it as a real curve and also offset. */
export const PARALLEL_EDGE_ROUNDNESS = 0.08;

const LEGACY_NODE_KEYS = [
  'font', 'size', 'labelHighlightBold', 'widthConstraint', 'heightConstraint',
  'borderWidth', 'shapeProperties', 'margin', 'brokenImage',
];
const LEGACY_EDGE_KEYS = ['smooth', 'selfReference', 'hidden', 'endPointOffset', 'length'];

export const idKey = (id) => String(id);
export const sameId = (a, b) => String(a) === String(b);
export const isSelfLoop = (edge) => sameId(edge.from, edge.to);

/** True when the node has no link to a different node (self-effects do not count). */
export const isIsolatedNode = (nodeId, edges) => !edges.some(
  (edge) => !isSelfLoop(edge) && (sameId(edge.from, nodeId) || sameId(edge.to, nodeId)),
);
export const connectsPair = (edge, a, b) => (
  (sameId(edge.from, a) && sameId(edge.to, b)) || (sameId(edge.from, b) && sameId(edge.to, a))
);
export const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

/** Next free integer id: one more than the largest integer-like id in `items`. */
export const nextId = (items) => 1 + items.reduce((max, item) => {
  const n = Number(item.id);
  return Number.isInteger(n) && String(item.id).trim() !== '' && n > max ? n : max;
}, 0);

export const signLabel = (sign) => (sign === NEGATIVE ? '–' : '+');

const normalizeNodeColor = (color) => {
  if (!color) return undefined;
  if (typeof color === 'string') return { background: color };
  const out = {};
  if (typeof color.background === 'string' && color.background) out.background = color.background;
  if (typeof color.border === 'string' && color.border) out.border = color.border;
  return Object.keys(out).length > 0 ? out : undefined;
};

export const normalizeNode = (raw = {}) => {
  const node = {};
  Object.keys(raw).forEach((key) => {
    if (!LEGACY_NODE_KEYS.includes(key)) node[key] = raw[key];
  });
  node.shape = SHAPES.includes(node.shape) ? node.shape : DEFAULT_SHAPE;
  if (node.label === undefined || node.label === null) node.label = node.id === undefined ? '' : String(node.id);

  if (!isFiniteNumber(node.width) && isFiniteNumber(raw.widthConstraint?.minimum)) {
    node.width = raw.widthConstraint.minimum;
  }
  if (!isFiniteNumber(node.height) && isFiniteNumber(raw.heightConstraint?.minimum)) {
    node.height = raw.heightConstraint.minimum;
  }
  if (!isFiniteNumber(node.width)) delete node.width;
  if (!isFiniteNumber(node.height)) delete node.height;
  if (!isFiniteNumber(node.x)) delete node.x;
  if (!isFiniteNumber(node.y)) delete node.y;

  const color = normalizeNodeColor(node.color);
  if (color) node.color = color; else delete node.color;

  if (node.physics !== false) delete node.physics;
  if (node.resized !== true) delete node.resized;
  if (!node.image) delete node.image;
  return node;
};

export const normalizeEdge = (raw = {}, onWarning = console.warn) => {
  const edge = {};
  Object.keys(raw).forEach((key) => {
    if (!LEGACY_EDGE_KEYS.includes(key)) edge[key] = raw[key];
  });

  const identity = `Edge ${raw.id ?? `${raw.from} → ${raw.to}`}`;
  if (Object.prototype.hasOwnProperty.call(raw, 'arrows')) {
    const type = raw.arrows?.to?.type;
    if (type !== 'triangle' && type !== 'circle') {
      throw new Error(`${identity}: unsupported or malformed arrow`);
    }
    edge.sign = type === 'circle' ? NEGATIVE : POSITIVE;
    if (Object.prototype.hasOwnProperty.call(raw, 'sign') && raw.sign !== edge.sign) {
      onWarning(`${identity}: corrected numeric sign from saved ${type} arrow`);
    }
  } else if (edge.sign !== POSITIVE && edge.sign !== NEGATIVE) {
    throw new Error(`${identity}: missing or invalid sign; expected numeric +1 or -1`);
  }

  if (!isFiniteNumber(edge.roundness)) {
    const { smooth } = raw;
    let roundness = 0;
    if (smooth && isFiniteNumber(smooth.roundness)) {
      if (smooth.type === 'curvedCW') roundness = smooth.roundness;
      else if (smooth.type === 'curvedCCW') roundness = -smooth.roundness;
    }
    edge.roundness = roundness;
  }

  if (!isFiniteNumber(edge.loopAngle)) {
    edge.loopAngle = isFiniteNumber(raw.selfReference?.angle) ? raw.selfReference.angle : DEFAULT_LOOP_ANGLE;
  }

  if (edge.color && typeof edge.color === 'object') edge.color = edge.color.color;
  if (!edge.color) delete edge.color;
  if (edge.dashes) edge.dashes = true; else delete edge.dashes;
  return edge;
};

/** Place nodes that have no coordinates on a ring around the nodes that do. */
export const assignPositions = (nodes) => {
  const placed = nodes.filter((node) => isFiniteNumber(node.x) && isFiniteNumber(node.y));
  const unplaced = nodes.filter((node) => !(isFiniteNumber(node.x) && isFiniteNumber(node.y)));
  if (unplaced.length === 0) return nodes;
  const centre = placed.reduce(
    (acc, node) => ({ x: acc.x + node.x / placed.length, y: acc.y + node.y / placed.length }),
    { x: 0, y: 0 },
  );
  const radius = Math.max(150, unplaced.length * 30);
  return nodes.map((node) => {
    const index = unplaced.indexOf(node);
    if (index === -1) return node;
    const angle = (index / unplaced.length) * Math.PI * 2;
    return {
      ...node,
      x: Math.round(centre.x + Math.cos(angle) * radius),
      y: Math.round(centre.y + Math.sin(angle) * radius),
    };
  });
};

/** Accepts a model in either the current or the legacy (vis-network) form. */
export const normalizeModel = (raw = {}, onWarning = console.warn) => {
  const nodeIds = new Map();
  const nodes = assignPositions((raw.nodes || []).map((node, index) => {
    const { id } = node;
    if (id === undefined || id === null) throw new Error(`Node at index ${index}: missing id`);
    const key = idKey(id);
    if (nodeIds.has(key)) {
      throw new Error(`Node IDs ${JSON.stringify(nodeIds.get(key))} and ${JSON.stringify(id)}: duplicate or normalization collision`);
    }
    nodeIds.set(key, id);
    return normalizeNode(node);
  }));
  const edgePairs = new Map();
  const edges = (raw.edges || []).map((edge) => {
    const identity = `Edge ${JSON.stringify(edge.id ?? [edge.from, edge.to])} (${JSON.stringify(edge.from)} → ${JSON.stringify(edge.to)})`;
    ['from', 'to'].forEach((endpoint) => {
      if (edge[endpoint] === undefined || edge[endpoint] === null) {
        throw new Error(`${identity}: missing endpoint ${endpoint}`);
      }
      if (!nodeIds.has(idKey(edge[endpoint]))) {
        throw new Error(`${identity}: undeclared endpoint ${endpoint}=${JSON.stringify(edge[endpoint])}`);
      }
    });
    const pair = JSON.stringify([idKey(edge.from), idKey(edge.to)]);
    if (edgePairs.has(pair)) {
      throw new Error(`${identity}: duplicate ordered edge pair; first declared by ${edgePairs.get(pair)}`);
    }
    edgePairs.set(pair, identity);
    return normalizeEdge(edge, onWarning);
  });
  const meta = raw.meta && typeof raw.meta === 'object' ? { ...raw.meta } : { title: 'Untitled' };
  return { ...raw, nodes, edges, meta };
};

export const toLegacyNode = (node) => {
  const next = { ...node };
  return next;
};

/** Adds the vis-network keys (`arrows`, `smooth`, `selfReference`) next to the current ones. */
export const toLegacyEdge = (edge) => {
  if (edge.sign !== POSITIVE && edge.sign !== NEGATIVE) {
    throw new Error(`Edge ${edge.id ?? `${edge.from} → ${edge.to}`}: missing or invalid sign`);
  }
  const roundness = isFiniteNumber(edge.roundness) ? edge.roundness : 0;
  const legacy = {
    ...edge,
    arrows: { ...edge.arrows, to: { ...edge.arrows?.to, type: edge.sign === NEGATIVE ? 'circle' : 'triangle' } },
    smooth: roundness === 0
      ? { type: 'straight', roundness: 0 }
      : { type: roundness > 0 ? 'curvedCW' : 'curvedCCW', roundness: Math.abs(roundness) },
  };
  if (isSelfLoop(edge)) {
    legacy.selfReference = { angle: isFiniteNumber(edge.loopAngle) ? edge.loopAngle : DEFAULT_LOOP_ANGLE };
  }
  return legacy;
};

export const toLegacyModel = ({ nodes = [], edges = [], meta = {}, ...context }) => ({
  ...context,
  nodes: nodes.map(toLegacyNode),
  edges: edges.map(toLegacyEdge),
  meta,
});
